// @feature-spec brain/vidmax/feature-specs/admin-model-registry.md
import { useCallback, useMemo } from "react"
import { InformationCircleIcon } from "@hugeicons-pro/core-solid-rounded"
import { HugeiconsIcon } from "@hugeicons/react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { z } from "zod"

import { AixContentHeader, Card, Loader } from "@aix/ui"
import { EModelAvailability } from "@aix/apiutils/src/models/configs"

import { api } from "~/utils/api"
import { ModelEvidenceDrawer } from "~/components/Admin/ModelRegistry/ModelEvidenceDrawer"
import { ModelRegistryFilters } from "~/components/Admin/ModelRegistry/ModelRegistryFilters"
import { ModelRegistryTable } from "~/components/Admin/ModelRegistry/ModelRegistryTable"
import type {
  AvailabilityFilter,
  EvidenceSummaryRow,
  RegistryEntry,
  RegistrySearch,
} from "~/components/Admin/ModelRegistry/types"
import CustomHead from "~/components/SEOHead/CustomHead"
import { SuperAdminLayout } from "~/layouts/SuperAdminLayout"

const SearchSchema = z.object({
  q: z.string().optional(),
  modality: z.enum(["video", "image", "audio", "text"]).optional(),
  availability: z.enum(["enabled", "disabled", "legacy", "coming-soon", "uncontracted"]).optional(),
  provider: z.string().optional(),
  mode: z.string().optional(),
  wiring: z.enum(["complete", "incomplete"]).optional(),
  id: z.string().optional(),
})

const EVIDENCE_WINDOW_DAYS = 30
const MODE_ORDER = ["t2v", "i2v", "r2v", "v2v"]

const AVAILABILITY_KEY: Record<EModelAvailability, AvailabilityFilter> = {
  [EModelAvailability.ENABLED]: "enabled",
  [EModelAvailability.DISABLED]: "disabled",
  [EModelAvailability.LEGACY]: "legacy",
  [EModelAvailability.COMING_SOON]: "coming-soon",
}

const availabilityKey = (entry: RegistryEntry): AvailabilityFilter =>
  entry.availability ? AVAILABILITY_KEY[entry.availability.status] : "uncontracted"

const matchesSearch = (entry: RegistryEntry, search: RegistrySearch): boolean => {
  if (search.q) {
    const needle = search.q.toLowerCase()
    if (!entry.canonicalId.toLowerCase().includes(needle) && !entry.displayName.toLowerCase().includes(needle)) {
      return false
    }
  }
  if (search.modality && String(entry.modality) !== search.modality) return false
  if (search.availability && availabilityKey(entry) !== search.availability) return false
  if (search.provider) {
    const providers = new Set([
      ...(entry.primaryProvider ? [entry.primaryProvider] : []),
      ...entry.providerMappings.map((mapping) => mapping.provider),
    ])
    if (!providers.has(search.provider)) return false
  }
  if (search.mode && !entry.modes.includes(search.mode)) return false
  if (search.wiring === "complete" && !entry.wiring.complete) return false
  if (search.wiring === "incomplete" && entry.wiring.complete) return false
  return true
}

function ModelRegistryPage() {
  const search = Route.useSearch()
  const navigate = useNavigate()

  const inventoryQuery = api.adminModelRegistry.getInventory.useQuery(undefined, { refetchOnWindowFocus: false })
  const evidenceQuery = api.adminModelRegistry.getEvidenceSummary.useQuery(
    { windowDays: EVIDENCE_WINDOW_DAYS },
    { refetchOnWindowFocus: false },
  )

  const entries = useMemo(() => inventoryQuery.data?.entries ?? [], [inventoryQuery.data?.entries])

  const updateSearch = useCallback(
    (patch: Partial<RegistrySearch>) => {
      void navigate({ to: "/admin/model-registry", search: { ...search, ...patch } })
    },
    [navigate, search],
  )

  const clearFilters = useCallback(() => {
    void navigate({ to: "/admin/model-registry", search: { id: search.id } })
  }, [navigate, search.id])

  const { evidenceByKey, unattributed } = useMemo(() => {
    const map = new Map<string, EvidenceSummaryRow>()
    let bucket: EvidenceSummaryRow | null = null
    for (const row of evidenceQuery.data?.rows ?? []) {
      if (row.isUnattributed) {
        bucket = row
      } else {
        map.set(row.modelKey, row)
      }
    }
    return { evidenceByKey: map, unattributed: bucket }
  }, [evidenceQuery.data?.rows])

  const providerOptions = useMemo(() => {
    const providers = new Set<string>()
    for (const entry of entries) {
      if (entry.primaryProvider) providers.add(entry.primaryProvider)
      for (const mapping of entry.providerMappings) providers.add(mapping.provider)
    }
    return Array.from(providers).sort()
  }, [entries])

  const modeOptions = useMemo(() => {
    const modes = new Set<string>()
    for (const entry of entries) for (const mode of entry.modes) modes.add(mode)
    const ordered = MODE_ORDER.filter((mode) => modes.has(mode))
    const extras = Array.from(modes)
      .filter((mode) => !MODE_ORDER.includes(mode))
      .sort()
    return [...ordered, ...extras]
  }, [entries])

  const filteredEntries = useMemo(() => entries.filter((entry) => matchesSearch(entry, search)), [entries, search])

  const counts = useMemo(() => {
    const byAvailability: Record<AvailabilityFilter, number> = {
      enabled: 0,
      disabled: 0,
      legacy: 0,
      "coming-soon": 0,
      uncontracted: 0,
    }
    for (const entry of entries) byAvailability[availabilityKey(entry)] += 1
    return { total: entries.length, filtered: filteredEntries.length, byAvailability }
  }, [entries, filteredEntries.length])

  const hasActiveFilters = Boolean(
    search.q || search.modality || search.availability || search.provider || search.mode || search.wiring,
  )

  const drawerEntry = useMemo(
    () => (search.id ? (entries.find((entry) => entry.canonicalId === search.id) ?? null) : null),
    [entries, search.id],
  )

  const isLoading = inventoryQuery.isLoading || evidenceQuery.isLoading

  return (
    <>
      <CustomHead title="Admin · Model Registry" />
      <AixContentHeader className="flex-col w-full border-b">
        <div className="w-full">
          <h1 className="text-xl font-semibold">Model Registry</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Every integrated model across video, image, audio, and text — with code-owned availability, provider/mode
            wiring, and recent output evidence.
          </p>
        </div>
      </AixContentHeader>

      <div className="space-y-4 p-6">
        <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <HugeiconsIcon icon={InformationCircleIcon} size={18} className="mt-0.5 shrink-0" />
          <div>
            <span className="font-medium">Read-only.</span> Model availability is owned by code, not the database. There
            is no toggle here — changing a model&apos;s availability requires a code PR. Each row&apos;s tooltip points
            at the source file to edit.
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader />
          </div>
        ) : (
          <>
            <ModelRegistryFilters
              search={search}
              onChange={updateSearch}
              providerOptions={providerOptions}
              modeOptions={modeOptions}
              counts={counts}
              hasActiveFilters={hasActiveFilters}
              onClear={clearFilters}
            />
            <Card className="overflow-x-auto border-none shadow-xs">
              <ModelRegistryTable
                entries={filteredEntries}
                evidenceByKey={evidenceByKey}
                unattributed={hasActiveFilters ? null : unattributed}
                onOpenDrawer={(id) => updateSearch({ id })}
              />
            </Card>
          </>
        )}
      </div>

      <ModelEvidenceDrawer
        modelId={search.id ?? null}
        entry={drawerEntry}
        onClose={() => updateSearch({ id: undefined })}
      />
    </>
  )
}

function AdminModelRegistryRoute() {
  return (
    <SuperAdminLayout>
      <ModelRegistryPage />
    </SuperAdminLayout>
  )
}

export const Route = createFileRoute("/admin/model-registry")({
  validateSearch: (search) => SearchSchema.parse(search),
  component: AdminModelRegistryRoute,
})
